import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, RegisterInput, CreateTenantInput } from '@maker-toolchest/shared';
import { useAuthStore } from '@/stores/auth.js';
import {
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
  createTenant as createTenantApi,
  initAuth,
  getOAuthUrl,
} from '@/lib/auth.js';

/**
 * Main auth hook - provides auth state and actions
 */
export function useAuth() {
  const {
    user,
    currentTenant,
    isLoading,
    isInitialized,
    accessToken,
    setCurrentTenant,
  } = useAuthStore();

  const queryClient = useQueryClient();

  // Initialize auth on mount
  useEffect(() => {
    if (!isInitialized) {
      initAuth();
    }
  }, [isInitialized]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => loginApi(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (input: RegisterInput) => registerApi(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => logoutApi(),
    onSuccess: () => {
      queryClient.clear();
    },
  });

  // Create tenant mutation
  const createTenantMutation = useMutation({
    mutationFn: (input: CreateTenantInput) => createTenantApi(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  return {
    // State
    user,
    currentTenant,
    isLoading,
    isInitialized,
    isAuthenticated: !!accessToken && !!user,

    // Actions
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    createTenant: createTenantMutation.mutateAsync,
    setCurrentTenant,
    getOAuthUrl,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}

/**
 * Hook to check if user has a specific role in current tenant
 */
export function useRole() {
  const { currentTenant } = useAuthStore();

  return {
    role: currentTenant?.role ?? null,
    isOwner: currentTenant?.role === 'OWNER',
    isAdmin: currentTenant?.role === 'ADMIN' || currentTenant?.role === 'OWNER',
    isMember: !!currentTenant?.role,
  };
}

/**
 * Hook to check subscription tier
 */
export function useTier() {
  const { currentTenant } = useAuthStore();

  return {
    tier: currentTenant?.tier ?? null,
    isTinkerer: currentTenant?.tier === 'TINKERER',
    isHobbyist: currentTenant?.tier === 'HOBBYIST',
    isBusiness: currentTenant?.tier === 'BUSINESS',
    isPaid: currentTenant?.tier === 'HOBBYIST' || currentTenant?.tier === 'BUSINESS',
  };
}
